import { Injectable, PLATFORM_ID, Inject, makeStateKey, TransferState } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformServer } from '@angular/common';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export interface OrderAddition {
  id: number;
  name: string;
  priceMultiplier: number;
  games: string[] | null;
  orderTypes: string[] | null;
  displayOrder: number;
  icon: string | null;
  title: string;
  tooltip?: string;
}

const ADDITIONS_KEY = (game: string, orderType: string, service: string, lang: string) =>
  makeStateKey<OrderAddition[]>(`order-additions-${game}-${orderType}-${service}-${lang}`);

@Injectable()
export class OrderAdditionsService {
  constructor(
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  getAvailableAdditions(game: string, lang = 'en', orderType?: string, service?: string): Observable<OrderAddition[]> {
    const stateKey = ADDITIONS_KEY(game, orderType || 'all', service || 'all', lang);

    const cached = this.transferState.get(stateKey, null);
    if (cached) {
      this.transferState.remove(stateKey);
      return of(cached);
    }

    let url = `/order-additions?game=${game}&lang=${lang}`;

    if (orderType) {
      url += `&orderType=${orderType}`;
    }

    if (service) {
      url += `&service=${service}`;
    }

    return this.http.get<OrderAddition[]>(url).pipe(
    map(additions => additions.sort((a, b) => a.displayOrder - b.displayOrder)),
    tap(additions => {
      if (isPlatformServer(this.platformId)) {
        this.transferState.set(stateKey, additions);
      }
    })
  );
  }

  calculateMultiplier(selectedAdditions: string[], allAdditions: OrderAddition[]): number {
    if (!selectedAdditions || selectedAdditions.length === 0) {
      return 1;
    }

    return selectedAdditions.reduce((multiplier, additionName) => {
      const addition = allAdditions.find(a => a.name === additionName);
      if (addition) {
        return multiplier * addition.priceMultiplier;
      }
      return multiplier;
    }, 1);
  }
}
